<template>
  <div class="info-container">
    <div class="main-text">
      <p>
        is Ukrainian photo-artist; founding member of the phenomenon later known as the Kharkiv School of
        Photography. In 1971, together with Yurii Rupin, he co-founded the group Vremia (Time), which included notable
        figures such as Boris Mikhailov, Oleg Maliovany, Oleksandr Suprun, Gennadiy Tubalev, Oleksandr Sytnychenko, and
        Anatolii Makiyenko. Vremia became a leading center of provocative and experimental photography in the Soviet
        Union and one of the first Ukrainian artistic voices recognized beyond the Iron Curtain. His artistic path spans
        from creating the first male-nude performance in the USSR (Violin, 1972) and developing an obstructive
        documentary style to producing post-religious fresco-like works that explore everyday life and metaphysical
        themes (Second Heaven, with Volodymyr Shaposhnykov, 2003).
      </p>
      <p>
        His works are held in numerous international museums, including the Centre Pompidou (Paris, France), Tate Modern
        (London, UK), the Museum of Fine Arts (Houston, USA), the Contemporary Arts Center (Cincinnati, USA), the
        Zimmerli Art Museum (New Jersey, USA), and the Cincinnati Art Museum (Cincinnati, USA).
      </p>
    </div>

    <div class="photo-container">
      <img src="@/assets/img.png" alt="Evgeniy Pavlov" />
    </div>

    <div class="columns-container">
      <div class="column">
        <h3>Selected Personal exhibitions:</h3>
        <div v-for="(item, index) in personalExhibitions" :key="'personal-' + index" class="exhibition-item">
          <p>{{ formatText(item) }}</p>
        </div>
      </div>
      <div class="column">
        <h3>Selected Group Exhibitions:</h3>
        <div v-for="(item, index) in groupExhibitions" :key="'group-' + index" class="exhibition-item">
          <p>{{ formatText(item) }}</p>
        </div>
      </div>
    </div>

    <div class="publications-container">
      <h3>Selected publications, articles and essays on Yevgeniy Pavlov's work</h3>
      <ol class="publications-list">
        <li v-for="(publication, index) in selectedPublications" :key="'pub-' + index">
          <a v-if="publication.url && publication.url !== '#'" :href="publication.url" target="_blank" rel="noopener noreferrer">{{ publication.title }}</a>
          <span v-else>{{ publication.title }}</span>
        </li>
      </ol>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { fetchInfoLinks } from "@/api";

// Initialize with hardcoded data first to avoid race condition
const personalExhibitions = ref([
  "2024, Total Photography, Vilniaus Fotografijos Galerija, Lithuanian Photographers Association.",
  "2022, Personal show within the framework of the International Festival «Freedome, the daughter of Art?», Schloss Grad, Grad, Slovenia, 07.08.22;",
  "2022, Personal show within the framework of the International Festival, Hochsommer, Pavel Haus, Austria.16.07-20.07.2022.",
  "2021, Personal show within the framework of the International Festival \"Dіchotomy. East and West \"Interfoto Bialystok'21,  26.09. - 10,10. 2021 (Bialystok, Poland). Catalog.",
  "2020, Evgeniy Pavlov. Violon, Archive series, Orvohrom in Kharkiv Photo Forum URL: http://kphf.com.ua/enах Kharkiv Photo Forum;",
  "2019, Evgeniy Pavlov. Freedom street (curator Maria Vtorushina), Set, Kiyv, Ukraine;",
  "2018, Chronicles of Total Photography (curator Tetyana Pavlova), Cultural and Information Center at the Ambassadа of Ukraine in Paris, the frame of Paris Photo 6-28 November;",
  "2017, UN HOMME, chapitre IV. La Mer Noire, le Gissant Doré, Institute of Romanian Culture in Paris, France;",
  "2017, Yevgeniy Pavlov,  Tetiana Pavlova. Method: Total Photography. Art Gallery INTERVENTSIA  Gallery, Red Building of Taras Shevchenko National University of Kyiv, October 26 – November 3 2017;",
  "2017, Chronicles of Total Photography (curator Tetyana Pavlova), National Dovzhenko Centre (in the frame of Gofol Fest), Kyiv (September, 07 – 17);",
  "2017, Total Photography, Come In Gallery, Kharkiv;",
  "2016 , La Nostalgie d'un (In)Visible. Yevgeniy Pavlov & Anna Orlowska. Rendez-vous photographique #2. Galerie Basia Embiricos, Paris;",
  "2015, Archive series in the frame of Paris Photo; Paris;",
  "2015, The Family album. Art Gallery 'Atelier Karas', Kyiv;",
  "2013, Home Life Book in presentation of film by О. Balagura and S. Zinovieva 'Life Span of the Object in Frame', Gajdaj Study (November 4, Kyiv);",
  "2011, Double Elvis Gallery, Stockholm (Sweden);",
  "2010, Exhibition \"Yakscho\", Museum of Contemporary Art PERMM, Perm, Russia;",
  "2009, Аrt Gallery Academia, Kharkiv;",
  "2007, Window (in the frame of International Biennale of artistic photography), Museum of History, Lviv;",
  "2006, 'AVEC' Art Gallery, Kharkiv;",
  "2005, The insight of Bonaparte (with V.Shaposhnikov) NaUKMA Art Gallery, in the frame of  Month of Photography in Kyiv",
  "2004, The Second Heaven (with V.Shaposhnikov), Municipal Art Gallery, Kharkiv;",
  "2002, Gallery Bastejs, Riga, Latvia;",
  "2001, Shut up! Art Gallery Palitrа, Kharkiv;",
  "2000, Detonation of hippy-end, Theatre of Opera and Ballet, Kharkiv;",
  "1999, Wolf Photographic Arts Gallery, Cincinnati, USA;",
  "1999, Ukrainian Culture Center, Kharkiv;",
  "1998, Museum of Fine Arts, Poltava;",
  "1998, Still lives (with V. Shaposhnikov), Art Gallery Palitra, Kharkiv;",
  "1998, Parnography (with V. Shaposhnikov), Art Gallery Exar, Ukraine; Museum of Fine Arts, Kharkiv;",
  "1997, Common Field (with V. Shaposhnikov), Art Gallery Atelier Karas, Kyiv;",
  "1996, Common Field (with V. Shaposhnikov), Artist's House, Kharkiv;",
  "1995, Rutgers University, New Jersey, USA;",
  "1995, Total Photography Museum Kyiv's Fortress, Kyiv;",
  "1994, Art Gallery Cypriana Majernika, International Month of Photography, Bratislava (Slovakia);",
  "1994, Total Photography Art Gallery Ukrainski zasiv, Kharkiv;",
  "1993, Art Gallery Lictblick, Köln (Germany);",
  "1993, Museum of Fine Arts, Kharkiv;",
  "1991, Contemporary Arts Center, Cincinnati, USA;",
  "1991, Rathaus, Keln (Germany);",
  "1990, Arm museum, Stockholm (Sweden);",
  "1988, House of Cinema, Kyiv;",
  "1988, Museum of Fine Arts, Kharkiv;"
]);

const groupExhibitions = ref([
  "2024, Love series within the framework of the International Exhibition Voices in the frame of Paris Photo.",
  "2024, Generations of Resilience, HANGAR Photo Centre, Brussels, Belgium, PhotoBrussels festival 2024, 25.01.24 - 23.03.24.",
  "2024, Ukrainian Dreamers. Charkiwer Schule der Fotografi. Kommunale Galerie, Berlin. 6. 03 - 2. 06.2024. https://www.kommunalegalerie-berlin.de/ausstellungen/ukrainian-dreamers?fbclid=IwAR029LMN7FFjr2GZyG5vRN9RgYYwGKPJ9t523r43WHBlXRn7tjXzhIrqJ44.",
  "2023, Ukrainian Dreamers, Kunstmuseum Wolfsburg (Wolfsburg, Germany). 12.10.23-07.01.24.",
  "2023, «Concentration of will», Center of Contemporary Art M17, Kyiv.",
  "2023, Ukraine. Une donation contemporaine. Center Pompidou (Paris, France).",
  "2023, Home. Perspectives, Open Eye Gallery, Liverpool (UK). 06.05.23",
  "2023, Ukraine. Une donation contemporaine. Center Pompidou (Paris, France).",
  "2023, «You Know That You Are Human @ POINTS of RESISTANCE V», Berlin (Germany), Zionskirche, 03.12.22 – 07.01.23. Mürsbach THEgallery 03.04.23, Galerie Eigenheim, Weimar URL: izolyatsia.org/en/project/you-know-that-you-are-human-points-of-resistance-v/you-know-that-you-are-human-points-of-resistance-v/",
  "2022, \"Violin\" in the frame of \"Paris Photo\" (Galerie nomade Alexandra De Viveiros), (Paris, France), 10-13.11.2022; Book signing 10.11.22;",
  "2022,The Road to Freedom, International Festival Noorderlicht, Kroningam, Kamp Vugh, Netherlands, 10.09.23-  07.2023;",
  "2022. L'Ecole de Kharkiv. Center Pompidou (Paris, France), from March 10, 2022, in solidarity with Ukraine;",
  "2022, «Once upon the war in KHARKIV» в Galerie nomade (Paris) in the frame of  Photo London Fair, 22.03 – 14 04. 2022. https://my.matterport.com/show/?m=kcGZH56D3BP&sr=-.16,.11&ss=14",
  "2022, \"Violin\" (Ilex Gallery, Warsaw) in the frame of  Photo London Fair, 11– 15.05 2022.",
  "2021, Group exhibition \"Ours in Pompidou\", Art Gallery. G. Semiradsky, Kharkiv, November 5-27, 2021;",
  "2021, International project \"Sensibility. Contemporary Ukrainian photography \". 29.07 – 10.10 2021, in the Mystetsky Arsenal (Kyiv). https://artarsenal.in.ua/povidomlennya/zaproshuyemo-na-vidkryttya-vystavky-chutlyvist-suchasna-ukrayinska-fotografiya/",
  "2021, \"The author in the game\", Yermilov Center, Kharkiv (April 26 - July 10) (1x7 series, \"Mythologies\");",
  "2021, \"Open Opportunity\", Center for Contemporary Art M17 (May 21 to July 25, 2021) (series Love)",
  "2019-2020, Through the lens: Unbending life, Ukrainian Institute of Modern Art, Chicago, Illinois, USA;",
  "2019-2020, The Forbidden Image (curators Biorn Geldhofe, Martin Kiffer, Alicia Knock) – PinchukArtCentre, Kyiv;",
  "2018, Instant Time (curators: Viktoriia Bavykina, Asia Tsisar), Kyiv, Mystetskyi arsenal. July 12 – August 12.",
  "2017, UN HOMME, chapitre IV. La Mer Noire, le Gissant Doré (curator Camilo Raakana). Institut Culturel Roumain de Paris. 3-27 November;",
  "2017, Ukrainian contemporary photographers. Cultural and Information Center at the Ambassadа of Ukraine in Paris, 10-30 November;",
  "2017, Research platform: Anonymous Society, PinchukArtCentre, Kyiv. October 27, 2017 – January 7, 2018;",
  "2017, Zone of turbulence, in the framework of the 5th Odessa Biennale, Museum of Contemporary Art Odessa;",
  "2017, Pure art, Mystetsky Arsenal, Kyiv;",
  "2016, Crimea: A No Man's Land // http://www.vasa-project.com/gallery/crimea/intro-part-1.php",
  "2016, Kharkiv School of Photography: Soviet Censorship to New Aesthetics: Part 3, Contemporary Photographers Exhibition 1 VASA on-line exhibition. http://vasa-project.com/gallery/ukraine-3-1/pavlov/index-pavlov.php",
  "2015, Kharkiv School of Photography: Soviet Censorship to New Aesthetics: Part 2 – late 1980's-90's. http://vasa-project.com/gallery/ukraine-2/pavlov/index-pavlov.php",
  "2015 Love and War in Ukraine, curated by M.Pedan, Panoptikon Fotografins hus, Stockholm, Sweden;",
  "2015, Kharkiv School of Photography: Soviet Censorship to New Aesthetics 1970-1980s VASA on-line exhibition. http://vasa-project.com/gallery/ukraine-1/pavlov/index-pavlov.php",
  "2014, Apples, Аrt Gallery \"Academia\", Kharkiv; 29 Sept. – 4 Oct.;",
  "2014, Biografie II – Wir. Fotogalerie Wien,Vienna, Austria, (cat.)  2-27 Sept.;",
  "2013, The act of defiance. Body as a protest in Kharkiv' photos 1970-2010. The Lumiere Brothers Center for Photography, Moscow (Russia);",
  "2012, Photofestival, Houston, USA;",
  "2012, 'Life Span of the Object in Frame', in the frame of Gogolfest, Kyiv;",
  "2012, Kharkiv school of photography. The Drawing lessons. Ukrainian Photography. The Drawing lessons in the frame of Arsenale, Kyiv;",
  "2012, 'Life Span of the Object in Frame', Kinopanorama, Kyiv;",
  "2011, The Drawing lessons: Ukrainian Photography. In the frame of International Photography Festival \"PIP\", Pingyao (China);",
  "2008, Behind Walls, Eastern Europe before 1989, NOORDERLICHT Photofestival, Fries Museum, Leeuwarden (the Netherlands);",
  "2007, Eastern European Festival. Cincinnati Art Museum, USA;",
  "2005, Photography from Ukraine. West Michigan University;",
  "2004, Kharkiv's Photography. Poznan, City Gallery \"Arsenal\";",
  "2004, Photoestafeta: From Rodchenko to our days / Museum 'Moscow House of Photography';",
  "2003, A Touring Ukrainian Photography Exhibit from Kharkiv 'Dialogue: Mentors and their Students\"' Elkin's Hall Exhibition Space, USA, 6-26 September, 2003; Cincinnati Public School System, March, 2004;",
  "2003, Kharkiv photography from 70-80-th. (International Biannual of Photography), Artgallery 'Gonchari', Kyiv;",
  "2003, The First Collection. Central House of Artists, Kyiv (cat.);",
  "2002 ,'The Cultural Hero'. Academy of Art and Design, Kharkiv;",
  "2001, Project: Museum Photography. Municipal Gallery, Kharkiv;",
  "2000, Kharkiv Photography in The Exhibition 'Deuchte Fotofriendshaft'. Nurenberg, Germany, (cat.);",
  "2000 'Photo and Video. Kharkiv–1960-90-ies', Contemporary Art Center 'Sovi-Art', Kyiv;",
  "2000, 'The Museum of Ukrainian Contemporary Art' – Kyiv, Ukrainian House;",
  "1999 , 'Сarte blanche a Boris Mikhailov' - Cite Internationale des Arts, Paris, France;",
  "1999, The Exhibition of the Ukrainian Art. Contemporary Russian Art Museum, Jersey City;",
  "1999, Festival of Postmodern Ukrainian Art. Kharkiv, Ukranian Cultural Center;",
  "1997, The Art of Contemporary photography in Eastern Europe. Museum Ken Damy, Brescia, Italy;",
  "1996, The Mirror that remembers. Exhibition of Kharkiv's and Netherlanders photographers, traveled in Netherlands;",
  "1995, Multiple Exposures Ukrainian Photography Today. Rutgers Art Center, Zimmerly museum, New Jersey, USA;",
  "1994, Art of Contemporary photography: Russia, Ukraine, Buellorussia. Moscow, Central House of Artists, (cat.)",
  "1993, Kharkiv Photography. The Museum of Industrial Culture, Nurenberg, Germany,",
  "1992, Past/present: Photography from the collection of the Museum of fine Arts, Houston, USA, (08.12.91—09.02.92) ;",
  "1991, Photoexhibition. Photocentrum of Stockholm, Sweden, (cat.);",
  "1990, Exhibition of Kharkiv's photographers/ Heb, Gallery-'4', Chehia;",
  "1989, Exhibition of American, Estonian and Kharkiv's photographers. On Kashirka Gallery, Moscow;",
  "1989 'Photohraphy-150', Manezh, Moscow;",
  "1988, New Soviet Photography. Finland, Sveden. Denmark;",
  "1988, Exhibition of Kharkiv's photogroup \"Time\" (International 'Photovacacie') – Bulgaria;",
  "1988, 'F-88'. «Giant» (Palace of Students), Kharkiv;",
  "1987 , 'F-87'. «Giant» (Palace of Students), Kharkiv;",
  "1983, Exhibition of the \"Vremya\" group, House of Science, Kharkiv;"
]);

const selectedPublications = ref([
  {
    title: "Paris Photo 2024: An Interview with Florence Bourgeois //L'œil de la Photographie.06.11.2024.",
    url: "https://loeildelaphotographie.com/en/paris-photo-2024-entretien-avec-florence-bourgeois/?utm_source=Liste+ODLP+nouvelle+version&utm_campaign=d08d636b83-EMAIL_CAMPAIGN_2024_11_05_07_23&utm_medium=email&utm_term=0_-d08d636b83-%5BLIST_EMAIL_ID%5D"
  },
  {
    title: "Svetlana Aleksijevitsj. Op zoek naar een getuige // De Groene Amsterdammer. Nr. 41 (10 oktober 2024), pp. 44-47 (Amsterdam, Netherlands)",
    url: ""
  },
  {
    title: "Charlotte Jean. La force de résistance de la photographie ukrainienne dans une expo à Bruxelles// BeauxArts (Paris), 25.02.2024.",
    url: "https://www.beauxarts.com/expos/a-bruxelles-une-expo-montre-la-force-de-resistance-des-photographes/"
  },
  {
    title: "Dans l'œil de Libé. EN IMAGES – À Bruxelles, les photographes ukrainiens à l'honneur // Libération (Paris), 17.02.2024.",
    url: ""
  },
  {
    title: "Ukrainian Dreamers. 05 Mar 2024 – 02 Jun 2024 // Art Facts.",
    url: ""
  },
  {
    title: "Wynants J.-M. \"Generations of Resilience\": tous les visages de l'Ukraine // Le Soir (Paris). 30.01.2024, 14:31.",
    url: ""
  },
  {
    title: "Shields Fiona. Resilience and bravery in Ukraine: PhotoBrussels Festival 2024 // The Guardian (London), 30.01.2024.",
    url: ""
  },
  {
    title: "Barshinova О. Miasto na granicy // Herito (Krakow, Poland), 2023, № 49, pp. 56–57.",
    url: ""
  },
  {
    title: "Charkiwer Schule für Fotografie: Ironische Prise, subtiler Trotz // TAZ (Berlin), 14.10.2023.",
    url: ""
  },
  {
    title: "Home. Perspectives [Catalogue], Liverpool (UK), 2023.",
    url: ""
  },
  {
    title: "Yevgeniy Pavlov // «7». Das Magazin der Sloweniennen und Slowenen in der Steiermark. Pawel Haus. Graz (Austria), 2022/2023, pp. 131–151.",
    url: ""
  },
  {
    title: "Les Cahiers du Témoignage chrétien, XIV, Hiver 2023.",
    url: ""
  },
  {
    title: "Mercier C. Quand l'École de Kharkiv en avait plein le dogme // Libération, #12965, 28 February 2023, pp. 25–26.",
    url: ""
  },
  {
    title: "Pavlova T. Ukrainian photo on the perimeter of the war // *You Know that You are Human* [Catalogue]. Berlin, 2022, pp. 14–19.",
    url: ""
  },
  {
    title: "Chekh N. Just playing: Valera and Natasha Cherkashin // Khudozhnya kulʹtura, 2022, № 18, pp. 146–153.",
    url: ""
  },
  {
    title: "Час життя об'єкта в кадрі [Wikipedia], 08.04.2022.",
    url: ""
  },
  {
    title: "The Path of Freedom [Catalogue]. Groningen (Netherlands), 2022, 177 p. *(The information front)*.",
    url: ""
  },
  {
    title: "You Know that You are Human [Catalogue]. Berlin, 2022.",
    url: ""
  },
  {
    title: "Szerszeń T. Mutilated images of history (Boris Mikhailov, Evgeniy Pavlov) // The Online Platform \"Ukrainian. Photographies\".",
    url: ""
  },
  {
    title: "Chernichko A. On Paris Photo… // Bird in Flight, 09.11.2022.",
    url: ""
  },
  {
    title: "The Violin by Pavlov // Blind (Paris), 22.11.2022.",
    url: ""
  },
  {
    title: "Dastakian A. Kharkiv: sous les bombes russes, une école de photographie singulière en danger (Alternative).",
    url: ""
  },
  {
    title: "Luca Fiore. Scatti di verità contro il regime di allora, che ora vuole tornare // Il Foglio, 25 March 2022, pp. 1–2.",
    url: ""
  },
  {
    title: "Luca Fiore. The Dissident Photographers of Ukraine (Alternative) // Aperture, 25 March 2022.",
    url: ""
  },
  {
    title: "Alex Merola. Photo London 2022: Top five fair highlights // 1000 Words (London).",
    url: ""
  },
  {
    title: "The Kharkiv School of Photography // The Guardian, 11.05.2022.",
    url: ""
  },
  {
    title: "Roxana Azimi (Londres). À Photo London, une diversité de regards sur l'Ukraine // Le Monde (Paris), 13.05.2022.",
    url: ""
  },
  {
    title: "Вибір експертів Bird in Flight: 17 головних фотопроєктів незалежної України // Bird in Flight.",
    url: ""
  },
  {
    title: "Dichotomy. East and West – Catalogue of the International Festival \"Dichotomy. East and West\", Interfoto Bialystok'21, Białystok, Poland.",
    url: ""
  },
  {
    title: "Інтерв'ю з арт-критиком Ханну Ееріканеном: \"Ті, хто бачить інакше\" (про харківське фотомистецтво).",
    url: ""
  },
  {
    title: "Chekh N. Simvolicheskoye puteshestviye \"Skripki\" Yevgeniya Pavlova…, 22.12.2021 // Photographer.",
    url: ""
  },
  {
    title: "Чех Н.; Павлов Є.; Павлова Т. Скрипка = Violin, Kyiv: Rodovid, 2018, 128 p.; published in *Вісник ХДАДМ*, 2021, №2, pp. 389–391.",
    url: ""
  },
  {
    title: "Révolution permanente. Art de l'Ukraine XX – début XXI siècles, A. Lozhkina, Paris: Nouvelles Éditions Place, 2020.",
    url: ""
  },
  {
    title: "Бернар-Ковальчук H. Гра проти апарату. Харківська школа фотографії, MOKSOP, 2020.",
    url: ""
  },
  {
    title: "Ложкіна А. Перманентна революція. Мистецтво України ХХ – початку ХХІ століття, ArtHuss, 2019, pp. 233–243.",
    url: ""
  },
  {
    title: "Євгеній Павлов: \"Была одна двигательная сила…\" // Your Art, 21.11.2019.",
    url: ""
  },
  {
    title: "Родик Я. Прикол як пам'ятка протесту // Україна молода, 13.08.2019, p. 11.",
    url: ""
  },
  {
    title: "Виставка \"Улица Свободы\" // Bird in Flight, 2019.",
    url: ""
  },
  {
    title: "Akaga A. Depuis l'Ukraine: Le temps d'Evgeniy Pavlov // L'œil de la Photographie, 21.11.2018.",
    url: ""
  },
  {
    title: "Yevgeniy Pavlov. Violin, Kyiv: RODOVID – Grafpom, 2018, 127 p.",
    url: ""
  },
  {
    title: "Smirna L. Century of nonconformism in Ukrainian visual art, Kyiv: Phoenix, 2017.",
    url: ""
  },
  {
    title: "Szerszeń T. Selfie with the wife // KORYDOR, 12.07.2017.",
    url: ""
  },
  {
    title: "Camilo Racana. Un Homme, chapitre IV. La Mer Noire, Paris, 2017.",
    url: ""
  },
  {
    title: "Photo Kyiv 2017 (Catalogue), Kyiv, 2017.",
    url: ""
  },
  {
    title: "Ukrainian Erotic Photography, Kyiv: Osnovy, 2017.",
    url: ""
  },
  {
    title: "Tomasz Szerszeń. Selfie with my Wife // Widok (Warsaw), 2016, Nr. 13.",
    url: ""
  },
  {
    title: "Pavlov E.; Pavlova T. Dom bycia // Widok (Warsaw), 2016, Nr. 13.",
    url: ""
  },
  {
    title: "Yevgeniy Pavlov. Life steeper, richest fantasy // Bird in Flight, 18.10.2016.",
    url: ""
  },
  {
    title: "Pavlova T. Ukrainian photography 1970–2000 // *The History of European Photography*, Vol. III, 2016, pp. 765–791.",
    url: ""
  },
  {
    title: "Crimea: A No Man's Land // *VASA Journal*, Issue #18, 2016.",
    url: "http://www.vasa-project.com/gallery/crimea/intro-part-1.php"
  },
  {
    title: "Yevgeniy Pavlov. Total Photography, Kyiv: ArtHuss, 2016, 112 p.",
    url: ""
  },
  {
    title: "Yevgeniy Pavlov. Kharkiv School of Photography, Art Ukraine, 16.09.2015.",
    url: ""
  },
  {
    title: "Catalogue 19th Paris Photo, Paris, 2015, p. 42.",
    url: ""
  },
  {
    title: "Pavlova T. Kharkiv Photography in Independent Ukraine // VASA Journal.",
    url: ""
  },
  {
    title: "Pavlov Y. Олександр Супрун // *Олександр Супрун. Обране (1967–1997)*, 2014, p. 142–143.",
    url: ""
  },
  {
    title: "Krasny Elke. Biography in Fotobuch, #53, Vienna, 2014, pp. 71, 91–93.",
    url: ""
  },
  {
    title: "Pavlova T. Late 1960s to 1980s: The Vremya Group's Time // VASA Journal.",
    url: ""
  },
  {
    title: "Ukrainian Photography 1939–1969 // *History of European Photography*, Vol. II, 2014, pp. 822–841.",
    url: ""
  },
  {
    title: "Manko I. The Kharkiv School of Fine Art Photography // VASA Journal, 2014, Issue #7.",
    url: ""
  },
  {
    title: "Krasny Elke. Biografie II – Wir in Bilder // Fotogalerie Wien, #278, 2014.",
    url: ""
  },
  {
    title: "Kupinska A. Olexandr Balahura… // Docudays UA (Kyiv), 2013.",
    url: ""
  },
  {
    title: "Yevgeniy Pavlov. Home Life Book, Kharkiv: Grafprom, 2013, 88 p.",
    url: ""
  },
  {
    title: "Prygodich N. Kharkiv's school of photography // 5,6, 2012, № 7.",
    url: ""
  },
  {
    title: "Contemporary Russian Photography, Photofest, Houston, 2012.",
    url: ""
  },
  {
    title: "Ponomarenko M. The symphony of images // Domus Design, 2010, №2 (77), p. 37.",
    url: ""
  },
  {
    title: "Kharkiv's photography // Fine-art, 2009, №1, pp. 13–21.",
    url: ""
  },
  {
    title: "Behind Walls, Eastern Europe before 1989, Catalogue of the 15th NOORDERLICHT Photofestival, 2008.",
    url: ""
  },
  {
    title: "Photoestafeta Catalogue, Moscow House of Photography, 2006.",
    url: ""
  },
  {
    title: "Sydor-Gibelinda O. The body as evidence // Stolichnyje novosti, #20, 2005.",
    url: ""
  },
  {
    title: "Lichkovakh V. \"Peredvizhnitstvo\": povernennya v maybutnê // Galereya, 2004, №4, p. 20.",
    url: ""
  },
  {
    title: "The Second Heaven, Lviv, 2004.",
    url: ""
  },
  {
    title: "Kharkiv Photography, Catalogue, Arsenal, Poznan, 2004.",
    url: ""
  },
  {
    title: "Exhibition in Latvia, Vaterpas, 2003.",
    url: ""
  },
  {
    title: "Plafond and emptiness, Vaterpas, 2002, #39.",
    url: ""
  },
  {
    title: "Photography: Cabinet Format, Vaterpas, 2002, #41.",
    url: ""
  },
  {
    title: "The Forest in Kharkiv Photography of 70–80s // Vestnik KAAD, #6, 2002.",
    url: ""
  },
  {
    title: "The Second Heaven // Imago 14, 2002.",
    url: ""
  },
  {
    title: "\"Violin\" and \"Fotografia\" // Fotografia, 2001, #4 (Poland).",
    url: ""
  },
  {
    title: "Grinevich V. Pavlov's dogs // Golos Ukrainy, 19.10.2001, #191.",
    url: ""
  },
  {
    title: "The Realm of Flora // Imago, 2001, #12 (Bratislava).",
    url: ""
  },
  {
    title: "Photo–Video. Kharkiv 1960–90s // Gallery, 2000, #1/3 (Kyiv).",
    url: ""
  },
  {
    title: "Photographical events in Ukraine 2000 // Imago, 2000, #10 (Bratislava).",
    url: ""
  },
  {
    title: "Ukrainian photographer Yevgeniy Pavlov // Imago, 2000, #10.",
    url: ""
  },
  {
    title: "Parnography // Kinets kintsem, 1999, Ivano-Frankivsk.",
    url: ""
  },
  {
    title: "Glaser A. Ukraine in the Russian Museum // Jersey City, 1999.",
    url: ""
  },
  {
    title: "Apocalipsis will begin there, Kharkiv: Maidan, 1999.",
    url: ""
  },
  {
    title: "Violin // PARTA, 1998, #2 (Kyiv).",
    url: ""
  },
  {
    title: "Photoart by Yevgeniy Pavlov // Photo/Video, 1998, #1 (Kyiv).",
    url: ""
  },
  {
    title: "Body // PARTA, 1997, #1 (Kyiv).",
    url: ""
  },
  {
    title: "Lyapin O. Common field of Art // Den, April 1997.",
    url: ""
  },
  {
    title: "Interartscape // Ukrainski Zasiv, 1997, #10–12 (Kharkiv).",
    url: ""
  },
  {
    title: "Photonews, Issue 31, 1997, Museum Ken Damy, Brescia.",
    url: ""
  },
  {
    title: "Common field of Art // Style and home, 1997, #2 (Kyiv).",
    url: ""
  },
  {
    title: "Total photography // Revu Photography, 1996, #1 (Kyiv).",
    url: ""
  },
  {
    title: "Photography by Evgeniy Pavlov // Ukrainski Zasiv, 1996, #1–3 (Kharkiv).",
    url: ""
  },
  {
    title: "Total photography // Mastatstva, 1996, #9 (Minsk).",
    url: ""
  },
  {
    title: "Interartscape // Imago, 1996/97, #3 (Bratislava).",
    url: ""
  },
  {
    title: "Imago: Central and Eastern Europe 1985–1995 // Winter 1995/96 (Bratislava).",
    url: ""
  },
  {
    title: "William Zimmer. Ukrainian Photos, Rich and Raw // The New York Times, 23 July 1995.",
    url: ""
  },
  {
    title: "Art of Contemporary Photography: Russia, Ukraine, Belarus, Moscow, 1994.",
    url: ""
  },
  {
    title: "Utsällningar Kulturarbetsförmedling, Fotocentrum Edition, Stockholm, 1991.",
    url: ""
  },
  {
    title: "Oven Findsen. Art Notes // The Cincinnati Enquirer, 15.01.1991.",
    url: ""
  },
  {
    title: "Dilna'89, Lubsko, Czechoslovakia, 1990.",
    url: ""
  },
  {
    title: "Images Ink, 1990, Vol. 5, № 3–4 (USA).",
    url: ""
  },
  {
    title: "Towe Matre. Gatfull och subtil systemkritiker // Svenska Dagbladet, 08.11.1990.",
    url: ""
  },
  {
    title: "Landscape with figure of Artist // Sovetskoje Photo, 1990, #11.",
    url: ""
  },
  {
    title: "Rosengren A. Fotografi från Kharkov i Ukraina, CCCR, Stockholm, February 1990.",
    url: ""
  },
  {
    title: "Contemporary Soviet Photographers: Maslov, Pavlov, Suprun, 1990, Vol.1, Issue 1.",
    url: ""
  },
  {
    title: "Stigneyev V.; Mihalkovich V. Poetics of Photography, Iskusstvo, Moscow, 1989.",
    url: ""
  },
  {
    title: "TAIDE Magazine, #2129, 1989.",
    url: ""
  },
  {
    title: "The world of Art Photography, Moscow: Planeta, 1989.",
    url: ""
  },
  {
    title: "Artistes et créateurs: pionniers de l'avant-garde rouge // Photo, #262, July 1989 (Paris).",
    url: ""
  },
  {
    title: "Eerikainen H.; Taneli Eskola. Toisinnäkijät, Helsinki: SN–Kirjat, 1988, p. 31.",
    url: ""
  },
  {
    title: "Landscape with the figure of the artist // Blgarske Foto, 1987, #11 (Sofia).",
    url: ""
  },
  {
    title: "Jan Sunderland. Ocena nadesłanych zdjęć // Fotografia (Warsaw), 1973, #1, pp. 10–11.",
    url: ""
  }
]);

// Helper function to extract URLs from publication text
function extractUrl(text) {
  const urlMatch = text.match(/URL:\s*(https?:\/\/[^\s]+)/);
  return urlMatch ? urlMatch[1] : '#';
}

// Format text function
const formatText = (text) => {
  return text;
};

// Try to update with API data in the background
(async () => {
  try {
    const fetchedLinks = await fetchInfoLinks();

    // Only update if we actually got data
    if (fetchedLinks && fetchedLinks.personalExhibitions && fetchedLinks.personalExhibitions.length > 0) {
      personalExhibitions.value = fetchedLinks.personalExhibitions;
    }

    if (fetchedLinks && fetchedLinks.groupExhibitions && fetchedLinks.groupExhibitions.length > 0) {
      groupExhibitions.value = fetchedLinks.groupExhibitions;
    }

    if (fetchedLinks && fetchedLinks.selectedPublications && fetchedLinks.selectedPublications.length > 0) {
      selectedPublications.value = fetchedLinks.selectedPublications;
    }
  } catch (error) {
    console.error('Failed to load API data in background:', error);
  }
})();
</script>


<style scoped>
.main-text {
  font-size: 21px;
  line-height: 1;
  margin-bottom: 40px;
}

.info-container {
  font-size: 21px;
  width: 100%;
}

.photo-container {
  display: flex;
  justify-content: center;
  margin: 40px 0;

  img {
    max-width: 100%;
    height: auto;
  }
}

.publications-container h3 {
  margin-left: -40px;
  font-weight: 100;
}

.publications-list a {
  color: #000;
  text-decoration: none;
}

.columns-container {
  display: flex;
  gap: 40px;
}


.column {
  flex: 1;
}

.photo-container img {
  max-width: 100%;
  height: auto;
}

.column h3 {
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: 100;
}

.exhibition-item p {
  font-size: 15px;
  line-height: 1;
  margin-bottom: 10px;
}

@media (max-width: 768px) {
  .columns-container {
    flex-direction: column;
  }

  .column {
    margin-bottom: 30px;
  }
}

/* publications */

.publications-container {
  margin-top: 40px;
  margin-left: 40px;
}

.publications-container h3 {
  margin-bottom: 20px;
  margin-left: -40px;
  font-size: 18px;
}

.publications-list {
  font-size: 15px;
  line-height: 1;
}

.publications-list li {
  margin-bottom: 10px;
}

.publications-list a {
  color: #000;
  text-decoration: none;
}

.publications-list a:hover {
  text-decoration: underline;
}

</style>
